import React, { useCallback, useEffect, useRef } from "react";
import L from "../libs/leaflet";

import { Toast } from "../components/toast";
import MapOverlay from "../components/MapOverlay";
import { buildMapButtons } from "../leaflet/controls/buildMapButtons";
import { buildMapSearchInput } from "../leaflet/controls/buildMapSearchInput";
import { getDefaultMarkerIcon } from "../leaflet/controls/getDefaultMarkerIcon";

function LoadingOverlay({ message = "Cargando..." }) {
  return (
    <div className="absolute inset-0 z-[9999] flex items-center justify-center bg-white/80 backdrop-blur-sm">
      <div className="text-center">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-gray-800" />
        <p className="select-none text-lg font-medium text-gray-800">
          {message}
        </p>
      </div>
    </div>
  );
}

function useWhenReady(getMap) {
  const pendingRef = useRef([]);

  const whenReady = useCallback(
    (fn) => {
      const map = getMap();
      if (map) {
        fn(map);
        return () => {};
      }
      pendingRef.current.push(fn);
      return () => {
        pendingRef.current = pendingRef.current.filter((x) => x !== fn);
      };
    },
    [getMap],
  );

  useEffect(() => {
    const map = getMap();
    if (!map) return;

    const pending = pendingRef.current;
    pendingRef.current = [];

    pending.forEach((fn) => {
      try {
        fn(map);
      } catch (err) {
        console.error("Error en whenMapReady:", err);
      }
    });
  }, [getMap]);

  return whenReady;
}

export default function useMapControl(mapRef) {
  const markersRef = useRef([]);
  const buttonRefs = useRef({});
  const searchRefs = useRef({});

  const overlayRotationRef = useRef({ angleDeg: 0, enabled: false });

  const getMap = useCallback(
    () => mapRef?.current?.map?.target ?? null,
    [mapRef],
  );
  const whenMapReady = useWhenReady(getMap);

  // ---------------------------
  // View controls
  // ---------------------------
  const setView = useCallback(
    (lat, lng, zoom = 16) =>
      whenMapReady((map) => map.setView([lat, lng], zoom)),
    [whenMapReady],
  );

  const setZoom = useCallback(
    (zoomLevel) => whenMapReady((map) => map.setZoom(zoomLevel)),
    [whenMapReady],
  );

  const zoomIn = useCallback(
    () => whenMapReady((map) => map.zoomIn()),
    [whenMapReady],
  );
  const zoomOut = useCallback(
    () => whenMapReady((map) => map.zoomOut()),
    [whenMapReady],
  );

  const getCenter = useCallback(() => {
    const map = getMap();
    return map ? map.getCenter() : null;
  }, [getMap]);

  const fitBounds = useCallback(
    (bounds, options = { padding: [30, 30], animate: true, duration: 0.5 }) => {
      whenMapReady((map) => {
        const leafletBounds = Array.isArray(bounds)
          ? L.latLngBounds(bounds)
          : bounds;
        if (leafletBounds?.isValid?.()) map.fitBounds(leafletBounds, options);
      });
    },
    [whenMapReady],
  );

  const setMaxBounds = useCallback(
    (bounds) => whenMapReady((map) => map.setMaxBounds(bounds)),
    [whenMapReady],
  );

  const resetView = useCallback(
    (center = [0, 0], zoom = 2) =>
      whenMapReady((map) => map.setView(center, zoom)),
    [whenMapReady],
  );

  // ---------------------------
  // Lock / Unlock
  // ---------------------------
  const lockMap = useCallback(() => {
    whenMapReady((map) => {
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      // map.getContainer().style.cursor = "not-allowed";
    });
  }, [whenMapReady]);

  const unlockMap = useCallback(() => {
    whenMapReady((map) => {
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      map.getContainer().style.cursor = "";
    });
  }, [whenMapReady]);

  // ---------------------------
  // Markers
  // ---------------------------
  const addMarker = useCallback(
    (lat, lng, options = {}) => {
      const map = getMap();
      if (!map) return null;

      const { icon, center = false, zoom = 16, unique = false } = options;

      if (unique && markersRef.current.length > 0) {
        markersRef.current.forEach((m) => map.removeLayer(m));
        markersRef.current = [];
      }

      let markerIcon;
      if (icon instanceof HTMLElement || typeof icon === "string") {
        markerIcon = L.divIcon({
          html: icon,
          className: "",
          iconSize: [30, 30],
          iconAnchor: [15, 30],
        });
      } else if (icon instanceof L.Icon) {
        markerIcon = icon;
      } else {
        markerIcon = getDefaultMarkerIcon();
      }

      const marker = L.marker([lat, lng], { icon: markerIcon }).addTo(map);
      markersRef.current.push(marker);

      if (center) map.setView([lat, lng], zoom, { animate: true });
      return marker;
    },
    [getMap],
  );

  const clearMarkers = useCallback(() => {
    const map = getMap();
    if (!map) return;
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];
  }, [getMap]);

  // ---------------------------
  // Controls: Buttons / Search
  // ---------------------------
  const addMapButtons = useCallback(
    (buttonsConfig) => {
      const controls = buildMapButtons(buttonsConfig);

      whenMapReady((map) => {
        controls.forEach((item, idx) => {
          item.control.addTo(map);

          const id = Array.isArray(buttonsConfig)
            ? buttonsConfig?.[idx]?.id
            : buttonsConfig?.id;

          if (id) {
            buttonRefs.current[id] = item;
          }
        });
      });

      return controls;
    },
    [whenMapReady],
  );

  const addMapSearch = useCallback(
    (searchConfig) => {
      whenMapReady((map) => {
        const SearchControl = buildMapSearchInput(searchConfig);
        const control = new SearchControl();
        control.addTo(map);

        const id = searchConfig?.id ?? "default-search";
        searchRefs.current[id] = control;
      });
    },
    [whenMapReady],
  );

  // ---------------------------
  // Slots UI
  // ---------------------------
  const showToast = useCallback(
    (message, { type = "info", duration = 3000 } = {}) => {
      mapRef.current?.setSlot?.(
        "toast",
        <div className="pointer-events-none absolute inset-x-0 bottom-5 z-[9999] flex justify-center">
          <div className="pointer-events-auto">
            <Toast
              show={true}
              message={message}
              type={type}
              duration={duration}
              onClose={() => mapRef.current?.clearSlot?.("toast")}
            />
          </div>
        </div>,
      );
    },
    [mapRef],
  );

  const showLoading = useCallback(
    (message = "Cargando...") => {
      mapRef.current?.setSlot?.(
        "loading",
        <LoadingOverlay message={message} />,
      );
    },
    [mapRef],
  );

  const hideLoading = useCallback(() => {
    mapRef.current?.clearSlot?.("loading");
  }, [mapRef]);

  const showOverlay = useCallback(
    (children) => {
      lockMap();
      mapRef.current?.setSlot?.(
        "overlay",
        <div className="pointer-events-auto absolute inset-0 z-[9000]">
          <MapOverlay>{children}</MapOverlay>
        </div>,
      );
    },
    [lockMap, mapRef],
  );

  const hideOverlay = useCallback(() => {
    mapRef.current?.clearSlot?.("overlay");
    unlockMap();
  }, [unlockMap, mapRef]);

  // ---------------------------
  // Overlay rotation
  // ---------------------------
  const enableOverlayRotation = useCallback(
    (angleDeg) => {
      overlayRotationRef.current.enabled = true;
      overlayRotationRef.current.angleDeg = angleDeg ?? 0;
      const map = getMap();
      map?.fire("overlayrotation:change", {
        angleDeg: overlayRotationRef.current.angleDeg,
      });
    },
    [getMap],
  );

  const disableOverlayRotation = useCallback(() => {
    overlayRotationRef.current.enabled = false;
    overlayRotationRef.current.angleDeg = 0;
    const map = getMap();
    map?.fire("overlayrotation:change", { angleDeg: 0 });
  }, [getMap]);

  const setOverlayRotationAngle = useCallback(
    (angleDeg) => {
      overlayRotationRef.current.angleDeg = angleDeg ?? 0;
      const map = getMap();
      map?.fire("overlayrotation:change", {
        angleDeg: overlayRotationRef.current.angleDeg,
      });
    },
    [getMap],
  );

  const getOverlayRotationAngle = useCallback(() => {
    return overlayRotationRef.current.enabled
      ? overlayRotationRef.current.angleDeg
      : 0;
  }, []);

  return {
    // ready util
    whenMapReady,

    // view
    setView,
    setZoom,
    zoomIn,
    zoomOut,
    getCenter,
    fitBounds,
    setMaxBounds,
    resetView,

    // lock
    lockMap,
    unlockMap,

    // controls
    addMapButtons,
    addMapSearch,

    // markers
    addMarker,
    clearMarkers,

    // UI
    showToast,
    showLoading,
    hideLoading,
    showOverlay,
    hideOverlay,

    // rotation
    enableOverlayRotation,
    disableOverlayRotation,
    setOverlayRotationAngle,
    getOverlayRotationAngle,
  };
}
